import { safeParseFloat, safeParseInt } from "lib/number";
import { getFieldValue } from "helpers/collection";
import { getInternalLookUp } from "helpers/internal-lookups";
import { IDoor, findDoorByExternalId } from "helpers/door-collection";
import { areAllItemsContainedInList } from "helpers/objUtils";
import { tableValueGetter, tableToMap, getValueByPosition, ITableMap } from "helpers/table-bag";
import {
    SizingErrors,
    SizeCurveStatus,
    Kinds,
    IGridItem,
    ItemFields,
    Item,
    SizePacksToleranceThreshold,
    ONE_SIZE,
    ONE_SIZE_CURVE_NAME,
    ValueTypes,
} from "constants/product-collection";
import { APIError } from "lib/errors";
import { round } from "./rounding";
import { get } from "lodash";
import { ISchemaKey } from "helpers/product-collection";
import {
    IAllocationDimensionValue,
    IAllocationDimension,
    getGetterBreadcrumbs,
    AllocationBagType,
    getAllocationBagValue,
    AllocationBag,
    flatToBagValues,
    getBagValuesByRowId,
    getBagValue,
} from "helpers/allocation-dimension";
import { nullish } from "helpers/nullish";
import { doesBulksAndAllocationsMatchLegacy, doesBulksAndAllocationsMatch } from "helpers/allocation";
import { getAndIncrementRequestId } from ".";
import memoizeOne from "memoize-one";
import { PackUnit } from "reducers/collection";
import { Map } from "immutable";

interface IFindSizeCurveFromLookupParams {
    sizeCurveSchema: ISchemaKey;
    merchantId: string;
    assortmentData: any;
    sizes: string[];
    sizeProfileNameField: string;
    selectedSizeRange: string;
    sizeCurve?: string;
    initialSizeStatus?: string;
    fieldToValueTypeMap?: Map<string, ValueTypes>;
}

interface ILookupSizeAllocationParams {
    sizeCurveSchema: ISchemaKey;
    sizeDistributionSchema: ISchemaKey;
    merchantId: string;
    assortmentData: IGridItem;
    doors: IDoor[];
    dates: IAllocationDimensionValue[];
    sizes: string[];
    curveSizes: string[];
    sizeCurveToUpdate?: string;
    allocationDimensions: IAllocationDimension[];
    fieldToValueTypeMap?: Map<string, ValueTypes>;
    allocationBag?: AllocationBag;
}

const SizeCurveSeparator = "##";
const buildSizeCurveValue = (doorExternalId: string, size: string) => `${doorExternalId}${SizeCurveSeparator}${size}`;

const getDataFromDependencies = (dependencies: string[], assortmentData: object) =>
    dependencies.reduce(
        (acc, key) => ({
            ...acc,
            [key]: getFieldValue(assortmentData, key),
        }),
        {},
    );

const throwDoNotMatchError = (): void => {
    throw new APIError("Any size curve matched was found", null, SizingErrors.NoMatch);
};

export const getSizeCurveByName = (
    sizeCurveResponse: any[],
    sizeCurveName: string,
    sizeProfileNameField: string,
): any | undefined => {
    const sizeCurveMatched = sizeCurveResponse?.find((it) => {
        return it[sizeProfileNameField]?.string_value === sizeCurveName;
    });

    return sizeCurveMatched;
};

export const sizeCurveContainsAllSizes = (sizeCurve: any, sizes: string[]) => {
    const curveSizes = Array.from(new Set(get(sizeCurve, [Kinds.SIZES, "value"], [])));

    if (sizes.length === 0 || curveSizes.length === 0) {
        return false;
    }

    return areAllItemsContainedInList(sizes, curveSizes);
};

const getSizeCurveMatched = (sizeCurveResponse: any[], sizes: string[], sizeProfileNameField: string): string => {
    const sizeCurveMatched = sizeCurveResponse?.find((it) => sizeCurveContainsAllSizes(it, sizes));

    return get(sizeCurveMatched, [sizeProfileNameField, "string_value"]);
};

const getPartialSizeCurveMatched = (
    sizeCurveResponse: any[],
    sizes: string[],
    sizeProfileNameField: string,
    selectedSizeRange: string,
): string => {
    const curveResponseMatchedByLabel = sizeCurveResponse?.find(
        (it) => get(it, [Kinds.SIZE_RANGE, "string_value"], "") === selectedSizeRange,
    );
    if (!curveResponseMatchedByLabel) {
        throwDoNotMatchError();
    }
    const sizesWithoutManuallyAdded = Array.from(
        new Set(get(curveResponseMatchedByLabel, [Kinds.SIZES, "value"], [])),
    ).reduce((acc: string[], size: string) => {
        if (sizes.includes(size)) {
            acc.push(size);
        }
        return acc;
    }, []) as string[];

    return getSizeCurveMatched(sizeCurveResponse, sizesWithoutManuallyAdded, sizeProfileNameField);
};

export const findOneFieldByKind = (kind: string, itemDisplayProperty: any[]) =>
    itemDisplayProperty.find((it) => it.kind === kind);

export const findSizeCurveFromLookup = async ({
    sizeCurveSchema,
    merchantId,
    assortmentData,
    sizes,
    sizeProfileNameField,
    selectedSizeRange,
    sizeCurve: existingSizeCurve,
    initialSizeStatus,
    fieldToValueTypeMap,
}: IFindSizeCurveFromLookupParams) => {
    let sizeCurveToUse = existingSizeCurve;
    const { interalLookup, getDependencies } = getInternalLookUp(
        sizeCurveSchema.kind,
        {
            ...sizeCurveSchema.lookup_plan,
            returnKeyValue: false,
        },
        merchantId,
        fieldToValueTypeMap,
    );

    let sizeStatus: string =
        initialSizeStatus === SizeCurveStatus.Partial ? SizingErrors.CurveContainsZeroUnits : SizeCurveStatus.Sized;
    const sizeCurveData = getDataFromDependencies(getDependencies(), assortmentData);

    // Filter lookup rows for only rows that can be used for sizing, matches all sizes OR has same size range.
    // By doing this, `interalLookup` will know if we should fallback to broader lookup
    const filterRowFn = (row: any) =>
        sizeCurveContainsAllSizes(row, sizes) || get(row, [Kinds.SIZE_RANGE, "string_value"], "") === selectedSizeRange;

    try {
        const sizeCurveResponse = await interalLookup({ data: sizeCurveData, filterFn: filterRowFn });
        sizeCurveToUse = existingSizeCurve || getSizeCurveMatched(sizeCurveResponse, sizes, sizeProfileNameField);

        if (!sizeCurveToUse) {
            const isOneSize = sizes.length === 1 && sizes[0] === ONE_SIZE;

            if (isOneSize) {
                sizeCurveToUse = ONE_SIZE_CURVE_NAME;
            } else {
                // If there is no size curve found for selected sizes,
                // lookup for size curve based on "non-manual" (partial) selected sizes.
                sizeCurveToUse = getPartialSizeCurveMatched(
                    sizeCurveResponse,
                    sizes,
                    sizeProfileNameField,
                    selectedSizeRange,
                );
                sizeStatus = SizingErrors.CurveContainsZeroUnits;
                if (!sizeCurveToUse) {
                    throwDoNotMatchError();
                }
            }
        } else if (existingSizeCurve) {
            // If there is pre-existing size curve, make sure size curve has all selected sizes. Else, it's "partial"
            const sizeCurve = getSizeCurveByName(sizeCurveResponse, sizeCurveToUse, sizeProfileNameField);
            if (
                (!sizeCurve || !sizeCurveContainsAllSizes(sizeCurve, sizes)) &&
                existingSizeCurve !== ONE_SIZE_CURVE_NAME
            ) {
                sizeStatus = SizingErrors.CurveContainsZeroUnits;
            }
        }
    } catch (error) {
        if (!(error instanceof APIError)) {
            console.warn("findSizeCurveFromLookup: unexpected error", error); // eslint-disable-line no-console
        }
        throw error;
    }

    return { sizeCurve: sizeCurveToUse, sizeStatus };
};

const allocateDoorsLegacy = (
    doors: IDoor[],
    sizes: string[],
    assortmentData: IGridItem,
    bulkBagMap: ITableMap,
    curve: any,
    dimension?: string,
) => {
    const prependDimension = dimension ? [dimension] : [];
    return doors.reduce((acc, { value: doorExternalId, id: doorId }) => {
        const sizeBulkRef = get(bulkBagMap, [...prependDimension, doorId]);
        if (sizeBulkRef) {
            const sizeBulk = getValueByPosition(assortmentData.bulks, sizeBulkRef);
            if (sizeBulk) {
                // Calc total percentage since they dont always add up to 100% if we take a subset of sizes
                const totalPercentages = sizes.reduce(
                    (total, size) => total + curve[buildSizeCurveValue(doorExternalId, size)],
                    0,
                );
                const unroundedQuantities = sizes.map(
                    (size) => (sizeBulk * curve[buildSizeCurveValue(doorExternalId, size)]) / totalPercentages,
                );
                const quantities = round(unroundedQuantities, sizeBulk);
                const allocation = sizes.map((size, index) => [
                    ...prependDimension,
                    doorId,
                    size,
                    quantities[index].toString(),
                ]);
                acc.push(allocation);
            }
        }
        return acc;
    }, []);
};

const allocateDoors = (
    doors: IDoor[],
    sizes: string[],
    assortmentData: IGridItem,
    bagValues: AllocationBag,
    curve: any,
    dimension?: string,
) => {
    const prependDimension = dimension ? [dimension] : [];
    const itemId = get(assortmentData, Item.FIELDS.ID);
    return doors.reduce((acc, { value: doorExternalId, id: doorId }) => {
        const quantityToAllocate = getAllocationBagValue(
            AllocationBagType.bulks,
            bagValues,
            itemId,
            prependDimension.concat([doorId]),
        );
        if (quantityToAllocate) {
            // Calc total percentage since they dont always add up to 100% if we take a subset of sizes
            const totalPercentages = sizes.reduce(
                (total, size) => total + curve[buildSizeCurveValue(doorExternalId, size)],
                0,
            );
            const unroundedQuantities = sizes.map(
                (size) => (quantityToAllocate * curve[buildSizeCurveValue(doorExternalId, size)]) / totalPercentages,
            );
            const quantities = round(unroundedQuantities, quantityToAllocate);
            const allocation = sizes.map((size, index) => [
                ...prependDimension,
                doorId,
                size,
                quantities[index].toString(),
            ]);
            acc.push(allocation);
        }
        return acc;
    }, []);
};

const logErrorOnEmptyDistributionSize = (
    doors: IDoor[],
    curveSizes: string[],
    assortmentData: IGridItem,
    bulkBagMap: ITableMap,
    curve: any,
    dimension?: string,
) => {
    const prependDimension = !nullish(dimension) && dimension !== "" ? [dimension] : [];

    doors.forEach(({ value: doorExternalId, id: doorId, label: doorLabel }) => {
        const sizeBulkRef = get(bulkBagMap, [...prependDimension, doorId]);
        const sizeBulk = sizeBulkRef && getValueByPosition(assortmentData.bulks, sizeBulkRef);
        if (!sizeBulk) {
            return;
        }

        const nullishCurve = curveSizes.find((size) => nullish(curve[buildSizeCurveValue(doorExternalId, size)]));
        if (nullishCurve) {
            // eslint-disable-next-line no-console
            console.log("Size bulked does not have distribution percentage:", {
                doorId,
                doorExternalId,
                doorLabel,
                size: nullishCurve,
                curveKey: buildSizeCurveValue(doorExternalId, nullishCurve),
                percentageDistributions: Object.keys(curve).reduce(
                    (acc, key) =>
                        key.startsWith(buildSizeCurveValue(doorExternalId, "")) ? { ...acc, [key]: curve[key] } : acc,
                    {},
                ),
            });
        }
    });
};

const getCurveMap = (sizeCurveResponse: any[], doors: IDoor[], sizes: string[]) =>
    doors.reduce((acc, { value: doorExternalId }) => {
        sizes.forEach((size) => {
            const intersection = sizeCurveResponse.find(
                (it) => get(it, [Kinds.DOOR_NUMBER, "string_value"]) === doorExternalId,
            );
            const index = get(intersection, [Kinds.SIZES, "value"], []).findIndex((it) => it === size);
            const percent =
                index >= 0 ? Number(get(intersection, [Kinds.PERCENT_DISTRIBUTION, "value"], [])[index]) : null;
            acc[buildSizeCurveValue(doorExternalId, size)] = percent; // ie .8028
        });

        return acc;
    }, {});

const getItemAllocationResultsLegacy = (
    dates: IAllocationDimensionValue[],
    doors: IDoor[],
    sizes: string[],
    curveSizes: string[],
    assortmentData: IGridItem,
    sizeCurveResponse: any[],
    allocationDimensions: IAllocationDimension[],
): { error?: string; data?: Array<Array<string | number>>; sizeCurve: any[] } => {
    const bulkBagMap = tableToMap(assortmentData.bulks);
    const hasGlobalGroup = !!(dates || []).length;

    // Find curve to use
    const curve = getCurveMap(sizeCurveResponse, doors, sizes);

    const globalGroups = [...(hasGlobalGroup ? dates : [{ id: "" } as any as IAllocationDimensionValue])];
    const allocations = globalGroups.reduce(
        (acc, date) => acc.concat(...allocateDoorsLegacy(doors, sizes, assortmentData, bulkBagMap, curve, date.id)),
        [],
    );
    const totalsMatch = doesBulksAndAllocationsMatchLegacy(
        { r: allocations.map((it) => ({ c: it })) },
        assortmentData.bulks,
        allocationDimensions,
    );

    globalGroups.forEach((date) =>
        logErrorOnEmptyDistributionSize(doors, curveSizes, assortmentData, bulkBagMap, curve, date.id),
    );

    return {
        error: totalsMatch ? null : SizingErrors.DoorCurveNotFound,
        data: allocations,
        sizeCurve: sizeCurveResponse,
    };
};

const getItemAllocationResults = (
    dates: IAllocationDimensionValue[],
    doors: IDoor[],
    sizes: string[],
    curveSizes: string[],
    assortmentData: IGridItem,
    sizeCurveResponse: any[],
    allocationDimensions: IAllocationDimension[],
    allocationBag: AllocationBag,
): { error?: string; data?: string[][]; sizeCurve: any[] } => {
    const hasGlobalGroup = !!(dates || []).length;
    const itemId = get(assortmentData, Item.FIELDS.ID);

    // Find curve to use
    const curve = getCurveMap(sizeCurveResponse, doors, sizes);

    const globalGroups = [...(hasGlobalGroup ? dates : [{ id: "" } as any as IAllocationDimensionValue])];

    const allocations = globalGroups.reduce(
        (acc, date) => acc.concat(...allocateDoors(doors, sizes, assortmentData, allocationBag, curve, date.id)),
        [],
    );
    const resultingBagValues = flatToBagValues(allocations);
    const bulks = getBagValuesByRowId(AllocationBagType.bulks, allocationBag, itemId);
    const totalsMatch = doesBulksAndAllocationsMatch(resultingBagValues, bulks, allocationDimensions);

    return {
        error: totalsMatch ? null : SizingErrors.DoorCurveNotFound,
        data: allocations,
        sizeCurve: sizeCurveResponse,
    };
};

export const lookupSizeAllocation = async ({
    sizeCurveSchema,
    sizeDistributionSchema,
    merchantId,
    assortmentData,
    doors,
    sizes,
    curveSizes,
    dates,
    sizeCurveToUpdate,
    allocationDimensions,
    fieldToValueTypeMap,
    allocationBag,
}: ILookupSizeAllocationParams) => {
    const { interalLookup, getDependencies } = getInternalLookUp(
        sizeCurveSchema.kind,
        {
            ...sizeDistributionSchema.lookup_plan,
            returnKeyValue: false,
        },
        merchantId,
        fieldToValueTypeMap,
    );

    const sizeCurveData = getDataFromDependencies(getDependencies(), {
        ...assortmentData,
        ...(sizeCurveToUpdate
            ? {
                  fields: {
                      ...assortmentData.fields,
                      [sizeCurveSchema.field_name]: {
                          string_value: sizeCurveToUpdate,
                      },
                  },
              }
            : {}),
    });

    let sizeCurveResponse = await interalLookup({ data: sizeCurveData });
    // If it's one size and there is no size curve from lookup, build size curve manually
    if (sizeCurveResponse.length === 0 && sizeCurveToUpdate === ONE_SIZE_CURVE_NAME) {
        sizeCurveResponse = oneSizedDummySizeCurve(doors);
    }

    return allocationBag
        ? getItemAllocationResults(
              dates,
              doors,
              sizes,
              curveSizes,
              assortmentData,
              sizeCurveResponse,
              allocationDimensions,
              allocationBag,
          )
        : getItemAllocationResultsLegacy(
              dates,
              doors,
              sizes,
              curveSizes,
              assortmentData,
              sizeCurveResponse,
              allocationDimensions,
          );
};

export const lookupSizeCurve = async ({
    sizeCurveSchema,
    sizeDistributionSchema,
    merchantId,
    assortmentData,
    sizeCurveToUpdate,
    doors,
    fieldToValueTypeMap,
}: ILookupSizeAllocationParams) => {
    const { interalLookup, getDependencies } = getInternalLookUp(
        sizeCurveSchema.kind,
        {
            ...sizeDistributionSchema.lookup_plan,
            returnKeyValue: false,
        },
        merchantId,
        fieldToValueTypeMap,
    );

    const sizeCurveData = getDataFromDependencies(getDependencies(), {
        ...assortmentData,
        ...(sizeCurveToUpdate
            ? {
                  fields: {
                      ...assortmentData.fields,
                      [sizeCurveSchema.field_name]: {
                          string_value: sizeCurveToUpdate,
                      },
                  },
              }
            : {}),
    });
    let sizeCurveResponse = await interalLookup({ data: sizeCurveData });
    // If it's one size and there is no size curve from lookup, build size curve manually
    if (sizeCurveResponse.length === 0 && sizeCurveToUpdate === ONE_SIZE_CURVE_NAME) {
        sizeCurveResponse = oneSizedDummySizeCurve(doors);
    }

    return sizeCurveResponse;
};

// Check if size curve has bad data. Criteria:
// - Store doesn't have consistent size family
export const sizeCurveHasBadData = (sizeCurve) => {
    // Check if size curve has matching selected sizes
    let lastSizeFamily;
    let isBadData = false;
    for (const _sizeCurve of sizeCurve) {
        if (lastSizeFamily !== undefined && lastSizeFamily !== _sizeCurve.SIZES.string_value) {
            isBadData = true;
            break;
        }

        lastSizeFamily = _sizeCurve.SIZES.string_value;
    }

    return isBadData;
};

export const NO_CURVE_REQUEST_ID = "NO_CURVE";

export enum AllocationQuality {
    OPTIMAL = "OPTIMAL",
    SUB_OPTIMAL = "SUB_OPTIMAL",
    INFEASIBLE = "INFEASIBLE",
}
interface ILegacyAllocationRequestData {
    assortment_id?: string;
    request_id: string;
    time_limit_ms?: number;
    size_curve: {
        sizes: string[];
        stores: {
            store_number: string;
            percent_distributions: string;
        }[];
    } | null;
    rows: {
        row_id: string;
        selected_sizes: string[];
        store_bulk: {
            [storeId: string]: number;
        }[];
        size_constraint?: { size: string; allocation: number }[];
    }[];
}

// These properties are used by downstream functions and not actual request data.
// Make sure you delete this before making request.
interface ILegacyAllocationRequestExtension extends ILegacyAllocationRequestData {
    breadcrumbs: string[];
}

interface IAllocationRequestData {
    request_id: string;
    time_limit_ms?: number;
    size_curve: {
        sizes: string[];
        stores: {
            store_number: string;
            percent_distributions: string;
        }[];
    } | null;
    rows: {
        row_id: string;
        max_packs: number;
        store_bulks: {
            store: string;
            bulk: number;
            tolerance_upper: number;
            tolerance_lower: number;
            max_store_packs: number;
        }[];
        size_packs: {
            name: string;
            quantities: {
                size: string;
                quantity: number;
            }[];
        }[];
    }[];
}

// These properties are used by downstream functions and not actual request data.
// Make sure you delete this before making request.
interface IAllocationRequestExtension extends IAllocationRequestData {
    breadcrumbs: string[];
}

export interface IAllocationResponseData {
    allocation_quality: AllocationQuality;
    messages: {
        message: string;
        row_id: string;
        severity: string;
        store_number: string;
    }[];
    request_id: string;
    rows: {
        row_id: string;
        stores: {
            store_number: string;
            allocations: string[];
        }[];
    }[];
}

export const SIZE_PACK_UNLIMITED_VALUE = -1;

const oneSizedDummySizeCurve = memoizeOne((doors) => {
    return doors.map((door) => {
        return {
            DOOR_NUMBER: {
                string_value: door.value,
                value: door.value,
                type: "STRING",
                schemaName: "STORE_NBR",
            },
            PERCENT_DISTRIBUTION: {
                schemaName: "",
                string_value: "1",
                type: "STRING",
                value: ["1"],
            },
            SIZES: {
                schemaName: "",
                type: "STRING",
                string_value: `${ONE_SIZE}`,
                value: [`${ONE_SIZE}`],
            },
        };
    });
});

export const legacySplitRowGroupBySizeCurveAndDimension = (
    assortmentsAllocated,
    bagValues?: AllocationBag,
): { [requestId: string]: ILegacyAllocationRequestExtension } => {
    const sizeCurveGroups = {};

    let prefixRequestId;
    for (const clientAllocatedRow of assortmentsAllocated) {
        if (!prefixRequestId) {
            prefixRequestId = getAndIncrementRequestId(clientAllocatedRow.collectionId);
        }

        const rowId = clientAllocatedRow.row.getIn(Item.FIELDS.ID);
        const baseRow = {
            row_id: rowId,
            selected_sizes: clientAllocatedRow.sizes,
            store_bulk: [],
        };
        const baseGroup = {
            assortment_id: clientAllocatedRow.collectionId,
            request_id: NO_CURVE_REQUEST_ID,
            size_curve: null,
            rows: [],
        };

        // Group row without curve together
        if (!clientAllocatedRow.sizeCurveName || !clientAllocatedRow.sizeCurve.length) {
            if (!sizeCurveGroups[NO_CURVE_REQUEST_ID]) {
                sizeCurveGroups[NO_CURVE_REQUEST_ID] = { ...baseGroup };
            }
            sizeCurveGroups[NO_CURVE_REQUEST_ID].rows.push({
                ...baseRow,
            });
            continue;
        }

        if (sizeCurveHasBadData(clientAllocatedRow.sizeCurve)) {
            // eslint-disable-next-line no-console
            console.warn(`${clientAllocatedRow.sizeCurveName} has bad curve data.`);
            continue;
        }

        // Use last dimension as store
        const allocationDimensions = [...clientAllocatedRow.allocationDimensions];

        // Do not get breadcrumbs down to lowest level (Door)
        const breadcrumbsBeforeLastDimension = getGetterBreadcrumbs(
            allocationDimensions,
            allocationDimensions.length - 1,
        );
        breadcrumbsBeforeLastDimension.forEach((breadcrumbs) => {
            const requestId = `${prefixRequestId}_${clientAllocatedRow.sizeCurveName}_${breadcrumbs.join("#")}`;
            const bulksByDoor = {};
            const sizeConstraintsMap = {};
            clientAllocatedRow.doors.forEach((door) => {
                const path = [...breadcrumbs, door.id];
                bulksByDoor[door.value] =
                    (bagValues
                        ? getAllocationBagValue(AllocationBagType.bulks, bagValues, rowId, path)
                        : tableValueGetter(clientAllocatedRow.row.get(ItemFields.Bulks)?.toJS(), path)) ?? 0;

                if (clientAllocatedRow.constraintAllocation) {
                    // Sum allocation of each size
                    clientAllocatedRow.sizes.forEach((size) => {
                        const path = [...breadcrumbs, door.id, size];
                        const sizeQty = bagValues
                            ? getBagValue(clientAllocatedRow.constraintAllocation, path)
                            : tableValueGetter(clientAllocatedRow.constraintAllocation, path);
                        sizeConstraintsMap[size] = (sizeConstraintsMap[size] ?? 0) + safeParseFloat(sizeQty, 0);
                    });
                }
            });

            if (!sizeCurveGroups[requestId]) {
                const doorPositionsById = clientAllocatedRow.doors.reduce((acc, door, index) => {
                    acc[door.value] = index;
                    return acc;
                }, {});
                sizeCurveGroups[requestId] = {
                    ...baseGroup,
                    size_curve: {
                        // `clientAllocatedRow.sizeCurve` is list of distributions per door for a size curve. They all have same SIZES.
                        // Taking SIZES from first element.
                        sizes: clientAllocatedRow.sizeCurve[0].SIZES.value,
                        stores: clientAllocatedRow.sizeCurve
                            .reduce((acc, distributionPerDoor) => {
                                // We need to send all doors inside a curve even if there is no bulk there
                                const doorInSizeCurve = clientAllocatedRow.doors.find(
                                    (door) => door.value === distributionPerDoor.DOOR_NUMBER.string_value,
                                );
                                if (doorInSizeCurve) {
                                    acc.push({
                                        store_number: doorInSizeCurve.value,
                                        percent_distributions: distributionPerDoor.PERCENT_DISTRIBUTION.value,
                                    });
                                }

                                return acc;
                            }, [])
                            .sort((a, b) => {
                                const indexA = doorPositionsById[a.store_number] ?? clientAllocatedRow.doors.length;
                                const indexB = doorPositionsById[b.store_number] ?? clientAllocatedRow.doors.length;

                                return indexA - indexB;
                            }),
                    },
                    request_id: requestId,
                    breadcrumbs,
                    rows: [],
                };
            }

            const sizeConstraints = Object.keys(sizeConstraintsMap).reduce((acc, size) => {
                acc.push({
                    size,
                    allocation: sizeConstraintsMap[size],
                });

                return acc;
            }, []);

            sizeCurveGroups[requestId].rows.push({
                ...baseRow,
                store_bulk: bulksByDoor,
                size_constraint: sizeConstraints.length > 0 ? sizeConstraints : undefined,
            });
        });
    }

    return sizeCurveGroups;
};

export const splitRowGroupBySizeCurveAndDimension = (
    assortmentsAllocated,
    allocationBag?: AllocationBag,
): { [requestId: string]: IAllocationRequestExtension } => {
    const sizeCurveGroups = {};

    let prefixRequestId;
    for (const clientAllocatedRow of assortmentsAllocated) {
        if (!prefixRequestId) {
            prefixRequestId = getAndIncrementRequestId(clientAllocatedRow.collectionId);
        }

        const rowId = clientAllocatedRow.row.getIn(Item.FIELDS.ID);
        const baseRow = {
            row_id: rowId,
            store_bulks: [],
            size_packs: [],
            max_packs: SIZE_PACK_UNLIMITED_VALUE,
        };
        const baseGroup = {
            request_id: NO_CURVE_REQUEST_ID,
            size_curve: null,
            rows: [],
        };

        // Group row without curve together
        if (!clientAllocatedRow.sizeCurveName || !clientAllocatedRow.sizeCurve.length) {
            if (!sizeCurveGroups[NO_CURVE_REQUEST_ID]) {
                sizeCurveGroups[NO_CURVE_REQUEST_ID] = { ...baseGroup };
            }
            sizeCurveGroups[NO_CURVE_REQUEST_ID].rows.push({
                ...baseRow,
            });
            continue;
        }

        if (sizeCurveHasBadData(clientAllocatedRow.sizeCurve)) {
            // eslint-disable-next-line no-console
            console.warn(`${clientAllocatedRow.sizeCurveName} has bad curve data.`);
            continue;
        }

        const allocationDimensions = [...clientAllocatedRow.allocationDimensions];
        const sizePacksTolerance = clientAllocatedRow.sizePacks.sizePacksTolerance;

        // Do not get breadcrumbs down to lowest level (Door)
        const breadcrumbsBeforeLastDimension = getGetterBreadcrumbs(
            allocationDimensions,
            allocationDimensions.length - 1,
        );
        for (const breadcrumbs of breadcrumbsBeforeLastDimension) {
            const requestId = `${prefixRequestId}_${clientAllocatedRow.sizeCurveName}_${breadcrumbs.join("#")}`;
            const bulksByDoor = [];
            const sizeConstraintsMap = {};
            let totalBulk = 0;
            clientAllocatedRow.doors.forEach((door) => {
                const toleranceValue = sizePacksTolerance?.value ?? 0;
                const toleranceDirection = sizePacksTolerance?.direction;
                const path = [...breadcrumbs, door.id];
                const doorBulk = allocationBag
                    ? getAllocationBagValue(AllocationBagType.bulks, allocationBag, rowId, path)
                    : (tableValueGetter(clientAllocatedRow.row.get(ItemFields.Bulks).toJS(), path) ?? 0);
                const toleranceOffset = Math.round((toleranceValue / 100) * doorBulk);
                totalBulk += doorBulk;
                bulksByDoor.push({
                    store: door.value,
                    bulk: doorBulk,
                    tolerance_upper: [SizePacksToleranceThreshold.Above, SizePacksToleranceThreshold.Both].includes(
                        toleranceDirection,
                    )
                        ? toleranceOffset
                        : 0,
                    tolerance_lower: [SizePacksToleranceThreshold.Below, SizePacksToleranceThreshold.Both].includes(
                        toleranceDirection,
                    )
                        ? toleranceOffset
                        : 0,
                    max_store_packs: clientAllocatedRow.sizePacks.maxPerDoor,
                });

                if (clientAllocatedRow.constraintAllocation) {
                    // Sum allocation of each size
                    clientAllocatedRow.sizes.forEach((size) => {
                        const path = [...breadcrumbs, door.id, size];
                        const sizeQty = allocationBag
                            ? getBagValue(clientAllocatedRow.constraintAllocation, path)
                            : tableValueGetter(clientAllocatedRow.constraintAllocation, path);
                        sizeConstraintsMap[size] = (sizeConstraintsMap[size] ?? 0) + safeParseFloat(sizeQty, 0);
                    });
                }
            });

            // Skip allocating zero door bulks
            if (totalBulk === 0) {
                continue;
            }

            if (!sizeCurveGroups[requestId]) {
                const doorPositionsById = clientAllocatedRow.doors.reduce((acc, door, index) => {
                    acc[door.value] = index;
                    return acc;
                }, {});
                sizeCurveGroups[requestId] = {
                    ...baseGroup,
                    size_curve: {
                        // `clientAllocatedRow.sizeCurve` is list of distributions per door for a size curve. They all have same SIZES.
                        // Taking SIZES from first element.
                        sizes: clientAllocatedRow.sizeCurve[0].SIZES.value,
                        stores: clientAllocatedRow.sizeCurve
                            .reduce((acc, distributionPerDoor) => {
                                // We need to send all doors inside a curve even if there is no bulk there
                                const doorInSizeCurve = clientAllocatedRow.doors.find(
                                    (door) => door.value === distributionPerDoor.DOOR_NUMBER.string_value,
                                );
                                if (doorInSizeCurve) {
                                    acc.push({
                                        store_number: doorInSizeCurve.value,
                                        percent_distributions: distributionPerDoor.PERCENT_DISTRIBUTION.value,
                                    });
                                }

                                return acc;
                            }, [])
                            .sort((a, b) => {
                                const indexA = doorPositionsById[a.store_number] ?? clientAllocatedRow.doors.length;
                                const indexB = doorPositionsById[b.store_number] ?? clientAllocatedRow.doors.length;

                                return indexA - indexB;
                            }),
                    },
                    request_id: requestId,
                    breadcrumbs,
                    rows: [],
                };
            }

            sizeCurveGroups[requestId].rows.push({
                ...baseRow,
                max_packs: clientAllocatedRow.sizePacks.maxPerProduct,
                store_bulks: bulksByDoor,
                size_packs: clientAllocatedRow.sizePacks.selectedSizePacks.reduce((acc, sizePack) => {
                    if (sizePack) {
                        acc.push({
                            id: sizePack.id,
                            name: sizePack.name,
                            quantities: sizePack.packUnits.map((packUnit: PackUnit) => ({
                                size: packUnit.normalized_key,
                                quantity: packUnit.units,
                            })),
                        });
                    }

                    return acc;
                }, []),
            });
        }
    }

    return sizeCurveGroups;
};

export const allocateInClient = (rowGroupRequest: ILegacyAllocationRequestExtension): IAllocationResponseData => {
    if (!rowGroupRequest.size_curve) {
        return {
            allocation_quality: AllocationQuality.INFEASIBLE,
            request_id: rowGroupRequest.request_id,
            rows: rowGroupRequest.rows.map((row) => ({
                row_id: row.row_id,
                stores: [],
            })),
            messages: [],
        };
    }

    // Find curve to use
    const curve = rowGroupRequest.size_curve.stores.reduce((acc, { store_number, percent_distributions }) => {
        rowGroupRequest.size_curve.sizes.forEach((size, sizeIndex) => {
            acc[buildSizeCurveValue(store_number, size)] = safeParseFloat(percent_distributions[sizeIndex], 0);
        });

        return acc;
    }, {});

    const rows = [];
    rowGroupRequest.rows.forEach((row) => {
        const rowAllocations = Object.keys(row.store_bulk).reduce((acc, storeNumber) => {
            const storeBulk = safeParseFloat(row.store_bulk[storeNumber], 0);

            // Calc total percentage since they dont always add up to 100% if we take a subset of sizes
            const totalPercentages = row.selected_sizes.reduce(
                (total, size) => total + curve[buildSizeCurveValue(storeNumber, size)],
                0,
            );
            const unroundedQuantities = row.selected_sizes.map((size) => {
                if (totalPercentages === 0) {
                    return 0;
                }

                const distribution = curve[buildSizeCurveValue(storeNumber, size)] || 0;
                return (storeBulk * distribution) / totalPercentages;
            });

            const allocation = round(unroundedQuantities, storeBulk);
            acc.push({
                store_number: storeNumber,
                allocations: row.selected_sizes.reduce((acc2, selectedSize, selectedSizeIndex) => {
                    acc2.push(allocation[selectedSizeIndex]);
                    return acc2;
                }, []),
            });

            return acc;
        }, []);

        const allocatedRow = {
            row_id: row.row_id,
            stores: rowAllocations,
        };
        rows.push(allocatedRow);
    });

    return {
        allocation_quality: AllocationQuality.OPTIMAL,
        request_id: rowGroupRequest.request_id,
        rows,
        messages: [],
    };
};

export const getAllocationsByRow = (allocationResponses: IAllocationResponseData[], splitRowGroups, doors: IDoor[]) => {
    const allocationsByRow = {};
    for (const allocationResponse of allocationResponses) {
        const allocationRequest = splitRowGroups[allocationResponse.request_id];

        if (!allocationRequest) {
            continue;
        }

        for (const allocatedRow of allocationResponse.rows) {
            const rowId = allocatedRow.row_id;
            allocationsByRow[rowId] = allocationsByRow[rowId] ?? [];

            if (allocationResponse.request_id === NO_CURVE_REQUEST_ID) {
                continue;
            }

            const sizes = allocationRequest.rows.find((row) => row.row_id === rowId)?.selected_sizes;
            if (!sizes) {
                continue;
            }

            for (const allocatedRowByStore of allocatedRow.stores) {
                const doorByExternalId = findDoorByExternalId(allocatedRowByStore.store_number, doors);
                if (!doorByExternalId) {
                    continue;
                }

                const rowAllocation = allocatedRowByStore.allocations.reduce((acc, allocation, index) => {
                    acc.push([...allocationRequest.breadcrumbs, doorByExternalId.id, sizes[index], allocation]);
                    return acc;
                }, []);

                allocationsByRow[rowId] = allocationsByRow[rowId].concat(rowAllocation);
            }
        }
    }

    for (const allocationResponse of allocationResponses) {
        for (const allocationMessage of allocationResponse.messages) {
            // If a row is not sized due to error, use empty array as allocation.
            // This is to ensure all rows sent for sizing always have allocation, thus downstream will receive consistent data.
            if (allocationMessage.row_id && !allocationsByRow[allocationMessage.row_id]) {
                allocationsByRow[allocationMessage.row_id] = [];
            }
        }
    }

    return allocationsByRow;
};

interface IGetSizePacksAllocationsByRowReturn {
    allocationsByRow: {
        [rowId: string]: string[][];
    };
    packAllocationsByRow: {
        [rowId: string]: string[][];
    };
}
export const getSizePacksAllocationsByRow = (
    allocationResponses: IAllocationResponseData[],
    splitRowGroups,
    doors: IDoor[],
): IGetSizePacksAllocationsByRowReturn => {
    const allocationsByRow = {};
    const packAllocationsByRow = {};
    for (const allocationResponse of allocationResponses) {
        const allocationRequest = splitRowGroups[allocationResponse.request_id];

        if (!allocationRequest) {
            continue;
        }

        for (const allocatedRow of allocationResponse.rows) {
            const accumulates: {
                [key: string]: {
                    breadcrumbs: string[];
                    quantity: number;
                };
            } = {};

            const rowId = allocatedRow.row_id;
            packAllocationsByRow[rowId] = packAllocationsByRow[rowId] ?? [];
            allocationsByRow[rowId] = allocationsByRow[rowId] ?? [];

            if (allocationResponse.request_id === NO_CURVE_REQUEST_ID) {
                continue;
            }

            const sizePacks = allocationRequest.rows.find((row) => row.row_id === rowId)?.size_packs;
            if (!sizePacks) {
                continue;
            }

            for (const allocatedRowByStore of allocatedRow.stores) {
                const doorByExternalId = findDoorByExternalId(allocatedRowByStore.store_number, doors);
                if (!doorByExternalId) {
                    continue;
                }

                allocatedRowByStore.allocations.forEach((allocation, index) => {
                    // Only take when allocation > 0
                    const packAllocationQuantity = safeParseInt(allocation, 0);
                    if (packAllocationQuantity > 0 && sizePacks[index]) {
                        const sizePack = sizePacks[index];

                        const packBreadcrumbs = [...allocationRequest.breadcrumbs, doorByExternalId.id];
                        const packAllocation = packBreadcrumbs.concat([sizePack.id, packAllocationQuantity]);
                        packAllocationsByRow[rowId].push(packAllocation);

                        sizePack.quantities.forEach((sizeUnit) => {
                            const quantity = sizeUnit.quantity * packAllocationQuantity;
                            const breadcrumbs = [...packBreadcrumbs, sizeUnit.size];
                            const stringBreadcrumbs = breadcrumbs.join("_");

                            if (!accumulates[stringBreadcrumbs]) {
                                accumulates[stringBreadcrumbs] = accumulates[stringBreadcrumbs] ?? {
                                    breadcrumbs,
                                    quantity: 0,
                                };
                            }
                            accumulates[stringBreadcrumbs].quantity += quantity;
                        });
                    }
                });
            }

            const rowAllocation = Object.keys(accumulates).reduce((acc, accumulateKey) => {
                acc.push([...accumulates[accumulateKey].breadcrumbs, accumulates[accumulateKey].quantity]);
                return acc;
            }, []);
            allocationsByRow[rowId] = allocationsByRow[rowId].concat(rowAllocation);
        }
    }

    for (const allocationResponse of allocationResponses) {
        for (const allocationMessage of allocationResponse.messages) {
            // If a row is not sized due to error, use empty array as allocation.
            // This is to ensure all rows sent for sizing always have allocation, thus downstream will receive consistent data.
            if (allocationMessage.row_id && !allocationsByRow[allocationMessage.row_id]) {
                allocationsByRow[allocationMessage.row_id] = [];
            }
        }
    }

    return {
        allocationsByRow,
        packAllocationsByRow,
    };
};

export const getInfeasibleRows = (
    allocationResponses: IAllocationResponseData[],
    splitRowGroups,
): { [rowId: string]: string[] } => {
    const infeasibleRows = {};
    for (const allocationResponse of allocationResponses) {
        const allocationRequest = splitRowGroups[allocationResponse.request_id];

        if (!allocationRequest) {
            continue;
        }

        if (allocationResponse.allocation_quality === AllocationQuality.INFEASIBLE) {
            allocationRequest.rows.forEach((row) => {
                infeasibleRows[row.row_id] = [];
            });
        }
    }

    return infeasibleRows;
};
